/**
 * The three incidents on the OEM+ Incidents page — each told twice: "as it
 * happened", and replayed through the agent chain on the data that existed.
 *
 * Source: `const INCIDENTS=[ … ]` at line 2294 of
 * `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html` (3 records).
 * Consumer: `renderIncident()` at line ~2331.
 *
 * Porting decisions
 * - `machine` was `IDS.L14` on the first record. `IDS` is `B.ids` (line 1836),
 *   a display-id map (`L14→L-02`, `L03→L-04`, …). Every `IDS.*` reference —
 *   in `machine`, in the chart title, in two `happened` lines and in two
 *   `replay` lines — is resolved to the literal id the demo renders.
 * - `sub`, `kpis`, `chart`, `happened` and `replay` were zero-argument arrows
 *   because they interpolate live demo state. `sub`, `caption` and `replaySub`
 *   turn out to contain no interpolation at all and are plain strings here.
 *   The rest are modelled as data:
 *   - KPI tuples `[label, value, unit, note]` → `IncidentKpi`.
 *   - Any `fmt$(wo.<field>)` inside a value, a note or a calc becomes an
 *     `IncidentTextPart` of kind `'cost'` naming the work-order field; the
 *     literal text around it is kept verbatim as `'text'` parts. `'all_in'` is
 *     the demo's `total_direct_cost_aud + downtime_cost_aud`.
 *     The work order to read the fields from is `Incident.workOrder`, taken
 *     from the `FIG(..., 'wos', {wo_id: …})` filters in the source.
 *   - `FIG(value, dataset, filter, {calc})` wrappers become the optional
 *     `source` (drill-down) and `calc` (tooltip) fields on the KPI, and the
 *     `source` on `chart.sub`.
 *   - Chart series came from `DM` (daily metric rows) or
 *     `B.incident_series[machine].rows`; that choice is captured as
 *     `IncidentSeriesSource`. For `'dailyMetric'` read `.value` / `.date`; for
 *     `'incidentSeries'` read `row[1]` / `row[0]`. Labels are `date.slice(5)`
 *     in both cases, exactly as the demo does.
 * - STRIPPED MARKUP, "as it happened" timeline:
 *   - `<b>…</b>` around the moment of failure → text kept, tags stripped, the
 *     bolded sentence listed in `emphasis` so a scene can re-apply it.
 *   - The inline document buttons (a `<button class="src">` whose click handler
 *     called `openDoc('eml:01')`) and the eight others like it → HANDLER
 *     REMOVED; each becomes an entry in
 *     `links` as `{ label, openDoc }`, which is data, not a handler string. The
 *     trailing space the button left behind is trimmed from `text`.
 * - STRIPPED MARKUP, Attribution KPI: the value was
 *   `<span style="color:var(--crit);font-size:18px">Maintenance practice</span>`.
 *   The span is dropped and the emphasis modelled as `emphasis: 'crit'` on the
 *   value, because the red colouring is the point of that tile.
 * - `replay` tuples `[when, agent, text]` → `IncidentReplayStep`; no markup.
 * - No clock or randomness is read anywhere: every date in this file is a
 *   literal from the demo narrative.
 *
 * Data Schema layer — pure data, no behaviour.
 */

/** A work-order money field, or the demo's derived all-in figure. */
export type IncidentCostField =
  | 'labour_cost_aud'
  | 'parts_cost_aud'
  | 'freight_cost_aud'
  | 'downtime_cost_aud'
  | 'total_direct_cost_aud'
  /** `total_direct_cost_aud + downtime_cost_aud`, as the demo computes it. */
  | 'all_in_cost_aud';

/** A run of copy: literal text, or a money field formatted with `fmt$()`. */
export type IncidentTextPart =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'cost'; readonly field: IncidentCostField };

/** A drill-down into the store behind a figure (the demo's `FIG(...)` wrapper). */
export interface IncidentFigureSource {
  /** Table key, e.g. `'wos'`, `'metric'`, `'rmstq'`, `'shift'`. */
  readonly dataset: string;
  /** Equality filter applied to that table. */
  readonly filter: Readonly<Record<string, string>>;
}

/** The headline figure on a KPI tile. */
export type IncidentKpiValue =
  /** A literal, e.g. `'6.4'` or `'Maintenance practice'`. */
  | { readonly kind: 'text'; readonly text: string; readonly emphasis?: 'crit' }
  /** A money field off the incident's work order, formatted with `fmt$()`. */
  | { readonly kind: 'cost'; readonly field: IncidentCostField };

/** One of the four tiles above the incident charts. */
export interface IncidentKpi {
  /** Tile eyebrow, e.g. `'First warning in data'`. */
  readonly label: string;
  readonly value: IncidentKpiValue;
  /** Small text next to the value, e.g. `'hours'` or `'not the component'`. */
  readonly unit: string;
  /** The line under the value. */
  readonly note: readonly IncidentTextPart[];
  /** Present when the figure was wrapped in a source drill-down. */
  readonly source?: IncidentFigureSource;
  /** Present when the figure carried a "how this is calculated" tooltip. */
  readonly calc?: readonly IncidentTextPart[];
}

/** Where the chart's points come from. */
export type IncidentSeriesSource =
  /** `DM.filter(d => d.machine_id === machine)` — read `.value` and `.date`. */
  | { readonly kind: 'dailyMetric'; readonly machine: string }
  /** `B.incident_series[machine].rows` — read `row[1]` and `row[0]`. */
  | { readonly kind: 'incidentSeries'; readonly machine: string };

/** One plotted series. */
export interface IncidentSeries {
  readonly source: IncidentSeriesSource;
  /** Stroke colour, as authored, e.g. `'var(--warn)'`. */
  readonly colour: string;
  readonly area: boolean;
  readonly dots: boolean;
}

/** Axis, marker and formatting options for the incident chart. */
export interface IncidentChartOptions {
  /** Index of the day the failure happened; drawn as a vertical rule. */
  readonly vline: number;
  /** Label on that rule, e.g. `'03 Sep failure'`. */
  readonly vlabel: string;
  readonly xTitle: string;
  readonly yTitle: string;
  /** Threshold line value. */
  readonly hline: number;
  /** Label on the threshold line, e.g. `'85 °C alarm'`. */
  readonly hlabel: string;
  /** Decimal places on the y axis, when the demo set one. */
  readonly dec?: number;
  /** Forced y-axis minimum, when the demo set one. */
  readonly min?: number;
}

/** The chart beside the incident illustration. */
export interface IncidentChart {
  readonly title: string;
  /** Subtitle text; the source link is appended where the `·` trails off. */
  readonly subText: string;
  /** The "source rows" drill-down that closed the subtitle. */
  readonly subSource: IncidentFigureSource;
  readonly series: readonly IncidentSeries[];
  readonly opts: IncidentChartOptions;
}

/** A document button that sat inside an "as it happened" line. */
export interface IncidentLink {
  /** Button text, e.g. `'email'`, `'report'`, `'bulletin'`. */
  readonly label: string;
  /** The `openDoc()` key the button carried. */
  readonly openDoc: string;
}

/** One line of the "As it happened" timeline. */
export interface IncidentMoment {
  /** The `<time>` column, e.g. `'05 Sep 09:38'`. */
  readonly when: string;
  /** The line, markup stripped. */
  readonly text: string;
  /** Substrings that were `<b>` in the source. */
  readonly emphasis?: readonly string[];
  /** Document buttons that followed the text. */
  readonly links?: readonly IncidentLink[];
}

/** One line of the "Replayed with the agent chain" timeline. */
export interface IncidentReplayStep {
  /** The `<time>` column, e.g. `'21 Aug'`. */
  readonly when: string;
  /** Agent that acted, rendered as a tag. */
  readonly agent: string;
  /** What it did. */
  readonly text: string;
}

/** One incident. */
export interface Incident {
  /** Picker key and `show('incident', id)` argument. */
  readonly id: string;
  /** Reference line under the title, e.g. `'IR-2026-014 · WO-26-11870'`. */
  readonly ref: string;
  /** Machine id, e.g. `'L-02'`. */
  readonly machine: string;
  /** Mine code. */
  readonly mine: string;
  /** Component, matched against the condition register by name. */
  readonly comp: string;
  /** Date as displayed, e.g. `'03 Sep 2026'`. */
  readonly date: string;
  readonly title: string;
  readonly status: string;
  /** The work order every cost field on this incident is read from. */
  readonly workOrder: string;
  /** The scene-setting paragraph. */
  readonly sub: string;
  readonly kpis: readonly IncidentKpi[];
  readonly chart: IncidentChart;
  /** Caption under the machine illustration. */
  readonly caption: string;
  readonly happened: readonly IncidentMoment[];
  /** Subtitle of the replay column. */
  readonly replaySub: string;
  readonly replay: readonly IncidentReplayStep[];
}

const txt = (text: string): IncidentTextPart => ({ kind: 'text', text });
const cost = (field: IncidentCostField): IncidentTextPart => ({ kind: 'cost', field });

/** The three incidents of the fortnight, in demo order. */
export const INCIDENTS: readonly Incident[] = [
  {
    id: 'WO-26-11842',
    ref: 'WO-26-11842',
    machine: 'L-02',
    mine: 'KG',
    comp: 'Hydraulic pump & drive',
    date: '03 Sep 2026',
    title: 'Hydraulic pump drive coupling sheared',
    status: 'Closed · reliability review · warranty declined',
    workOrder: 'WO-26-11842',
    sub: 'Wed 03 Sep 2026, Kalbarri Gold (WA), 280 level. Hydraulic pump drive coupling sheared at 14:21 after 3,640 h. No live data at Kalbarri — the onboard log was read when the technician arrived. Left: what happened. Right: the same fortnight replayed through the agent chain on the Pitram shift-end export alone.',
    kpis: [
      {
        label: 'Downtime',
        value: { kind: 'text', text: '6.4' },
        unit: 'hours',
        source: { dataset: 'wos', filter: { wo_id: 'WO-26-11842' } },
        note: [
          txt('14:22 → 20:46 · '),
          cost('downtime_cost_aud'),
          txt(' lost production at A$1,100/h (loader, Kalbarri)'),
        ],
      },
      {
        label: 'All-in cost',
        value: { kind: 'cost', field: 'all_in_cost_aud' },
        unit: 'vs ≈ A$3,900 planned',
        source: { dataset: 'wos', filter: { wo_id: 'WO-26-11842' } },
        calc: [
          txt('Labour '),
          cost('labour_cost_aud'),
          txt(' + parts '),
          cost('parts_cost_aud'),
          txt(' + freight '),
          cost('freight_cost_aud'),
          txt(' + lost production '),
          cost('downtime_cost_aud'),
        ],
        note: [
          txt('Labour '),
          cost('labour_cost_aud'),
          txt(' · parts '),
          cost('parts_cost_aud'),
          txt(' · freight '),
          cost('freight_cost_aud'),
        ],
      },
      {
        label: 'First warning in data',
        value: { kind: 'text', text: '14' },
        unit: 'days before failure (20 Aug)',
        note: [txt('Hydraulic oil temp at shift end 74 → 88 °C in Pitram; 2 oil samples overdue in SAP')],
      },
      {
        label: 'Attribution',
        value: { kind: 'text', text: 'Maintenance practice', emphasis: 'crit' },
        unit: 'not the component',
        note: [txt('KG Night B · warranty declined CS-2026-2210 · 2nd early coupling at Kalbarri')],
      },
    ],
    chart: {
      title: 'Hydraulic oil temperature · shift-end daily mean · L-02',
      subText: 'Pitram export. Rising for two weeks, back to baseline after the flush · ',
      subSource: { dataset: 'metric', filter: { machine_id: 'L-02' } },
      series: [
        { source: { kind: 'dailyMetric', machine: 'L-02' }, colour: 'var(--warn)', area: true, dots: true },
      ],
      opts: {
        vline: 5,
        vlabel: '03 Sep failure',
        xTitle: 'Date (2026)',
        yTitle: 'Hydraulic oil temperature (°C)',
        hline: 85,
        hlabel: '85 °C alarm',
      },
    },
    caption: 'Component location: main hydraulic pump and drive coupling, rear frame LH side',
    happened: [
      {
        when: '20 Aug',
        text: 'Hydraulic oil temperature starts climbing in the shift-end export. Second 500 h oil sample now overdue. Nobody opens the export.',
        links: [{ label: 'debrief', openDoc: 'mtg:MTG-2026-0904.txt' }],
      },
      {
        when: 'Aug',
        text: '14 bucket-ramming events logged on L-02, 9 on Night B. Counted by the FMS, not reported to anyone.',
      },
      {
        when: '03 Sep 14:21',
        text: 'Coupling shears. E-HYD-PRESS-LOW then E-HYD-PUMP-DRIVE on the onboard log. Operator loses boom, bucket, steering. Parks in the drive.',
        emphasis: ['Coupling shears.'],
      },
      {
        when: '14:31',
        text: 'Supervisor emails the technician — "we\'re a loader short tonight already".',
        links: [{ label: 'email', openDoc: 'eml:01' }],
      },
      {
        when: '15:05',
        text: 'Technician on site, reads the log for the first time, opens the compartment: coupling in pieces, return filter full of metal, oil "like coffee". Last sample 2,140 h ago.',
      },
      {
        when: '15:20',
        text: 'Needs two drums and a filter cart — not on the truck. Orders a second coupling overnight from Perth DC; site will be at zero.',
        links: [{ label: 'email', openDoc: 'eml:02' }],
      },
      {
        when: '20:46',
        text: 'Return to service after full flush. Post-flush ISO 19/17/14 — still not in spec. Follow-up sample at +50 h.',
        links: [
          { label: 'report', openDoc: 'doc:WO-26-11842_Breakdown-Report_L-02_Kalbarri_2026-09-03.docx' },
        ],
      },
      {
        when: '04 Sep',
        text: 'Warranty declined — contamination and non-compliance. Superintendent: "the warning signs were in the data and nobody was looking".',
        links: [{ label: 'email', openDoc: 'eml:05' }],
      },
    ],
    replaySub: 'Back-test on the Pitram shift-end export and SAP PM only — no live data needed',
    replay: [
      {
        when: '20 Aug',
        agent: 'Diagnostic',
        text: 'Flags +6 °C hydraulic temperature drift against baseline on a machine with an overdue oil sample — TB-2026-031 Watch condition, 0.62.',
      },
      {
        when: '20 Aug',
        agent: 'Knowledge',
        text: 'Surfaces the 250 h sampling rule and the contamination flush procedure; notes L-04 failed the same way in February.',
      },
      {
        when: '21 Aug',
        agent: 'Work order',
        text: 'Drafts an oil-sample task for the next crib (30 min) and a planned coupling inspection.',
      },
      {
        when: '21 Aug',
        agent: 'Parts & logistics',
        text: 'KG store holds 0 couplings against min 1 — drafts a stock order, road freight, 1 day.',
      },
      {
        when: '24 Aug',
        agent: 'Fleet learning',
        text: 'Abuse-event count on L-02 (14/month) crosses the TB-2026-031 threshold — flags Night B for supervisor review.',
      },
      {
        when: '25 Aug',
        agent: 'Orchestrator',
        text: 'Sample result ISO 21/18/15 confirms contamination at 0.88 → planned flush and coupling change at the weekend PM, 4.5 h, A$3.9k.',
      },
      {
        when: 'Sep',
        agent: 'Work order',
        text: 'No breakdown. No overnight freight. No warranty argument. Cost ledger attributes the flush to maintenance practice so the trend is visible at the Thursday review.',
      },
    ],
  },
  {
    id: 'IR-2026-014',
    ref: 'IR-2026-014 · WO-26-11870',
    machine: 'S-05',
    mine: 'OH',
    comp: 'DTH hammer & pipes',
    date: '05 Sep 2026',
    title: 'Drill string parted in the hole — hammer and two pipes fished',
    status: 'Closed · incident review open (CS-2026-2215)',
    workOrder: 'WO-26-11870',
    sub: 'Fri 05 Sep 2026, Ochre Hill Gold (NT), main pit, pattern 412-B hole 14. The MS-120 string parted at its second joint 28 m down at 09:38. Live RMS feed over site LTE — the breakout-torque signature had been in the data for twelve days; the site had no threshold on it. Left: what happened. Right: the fortnight replayed with the torque trend enabled.',
    kpis: [
      {
        label: 'Downtime',
        value: { kind: 'text', text: '9.2' },
        unit: 'hours',
        source: { dataset: 'wos', filter: { wo_id: 'WO-26-11870' } },
        note: [
          txt('09:38 → 20:50 · '),
          cost('downtime_cost_aud'),
          txt(' lost production at A$950/h (blasthole drill, Ochre Hill)'),
        ],
      },
      {
        label: 'All-in cost',
        value: { kind: 'cost', field: 'all_in_cost_aud' },
        unit: 'vs ≈ A$4,600 planned pipe change',
        source: { dataset: 'wos', filter: { wo_id: 'WO-26-11870' } },
        calc: [
          txt('Labour '),
          cost('labour_cost_aud'),
          txt(' + parts '),
          cost('parts_cost_aud'),
          txt(' (3 pipes + bit) + freight '),
          cost('freight_cost_aud'),
          txt(' + lost production '),
          cost('downtime_cost_aud'),
        ],
        note: [
          txt('Labour '),
          cost('labour_cost_aud'),
          txt(' · parts '),
          cost('parts_cost_aud'),
          txt(' · freight '),
          cost('freight_cost_aud'),
        ],
      },
      {
        label: 'First warning in data',
        value: { kind: 'text', text: '12' },
        unit: 'days before failure (24 Aug)',
        note: [
          txt(
            'Breakout torque 7.1 → 10.4 kN·m in the RMS make-up/breakout log; two W-BREAKOUT-TORQUE warnings with no threshold',
          ),
        ],
      },
      {
        label: 'Attribution',
        value: { kind: 'text', text: 'Inspection standard', emphasis: 'crit' },
        unit: '+ make-up over-torque',
        note: [
          txt(
            'Thread wear past limit; last two joints made up at 10.9 and 11.2 kN·m against 8.5 · not the hammer, not the ground',
          ),
        ],
      },
    ],
    chart: {
      title: 'Joint breakout torque · daily mean · S-05 (RMS make-up/breakout log)',
      subText:
        'Meridian RMS over site LTE. Thread wear shows as rising breakout torque with flat air and penetration · ',
      subSource: { dataset: 'rmstq', filter: { machine_id: 'S-05' } },
      series: [
        {
          source: { kind: 'incidentSeries', machine: 'S-05' },
          colour: 'var(--warn)',
          area: true,
          dots: true,
        },
      ],
      opts: {
        vline: 9,
        vlabel: '05 Sep parted',
        xTitle: 'Date (2026)',
        yTitle: 'Breakout torque (kN·m)',
        hline: 9.5,
        hlabel: '9.5 kN·m Act',
        dec: 1,
        min: 6,
      },
    },
    caption: 'Component location: drill string — rotary head adapter, pipes and DTH hammer below the table',
    happened: [
      {
        when: '24 Aug',
        text: 'Breakout torque on the 412-B string starts rising in the RMS log — 7.1 kN·m and climbing. The site has no threshold on the signal.',
      },
      {
        when: 'Tue 02 Sep',
        text: 'Operator writes "joints getting hard to break" on the plod. Nobody reads plods for that.',
        links: [{ label: 'review', openDoc: 'mtg:MTG-2026-0906.txt' }],
      },
      {
        when: '03–04 Sep',
        text: 'Two W-BREAKOUT-TORQUE warnings logged by the RMS. Two joints made up at 10.9 and 11.2 kN·m against 8.5.',
      },
      {
        when: '05 Sep 09:38',
        text: 'String parts at the second joint, 28 m down. E-PIPE-PARTED, E-ROT-OVERSPEED. Hammer and two pipes in the hole. Supervisor moves S-03 onto the pattern.',
        emphasis: ['String parts at the second joint, 28 m down.'],
        links: [{ label: 'email', openDoc: 'eml:14' }],
      },
      { when: '10:20', text: 'Technician on the bench with the overshot from the workshop. Three attempts.' },
      {
        when: '17:55',
        text: 'Hammer and both pipes recovered. Remaining pipes gauged on the rack: three condemned, two dressed.',
      },
      {
        when: '20:50',
        text: 'Return to service with four new pipes from the site store; replenishment SO-90424 from Darwin. Twenty-four thousand all-in.',
        links: [{ label: 'email', openDoc: 'eml:15' }],
      },
      {
        when: '07 Sep',
        text: 'IR-2026-014 issued; TB-2026-033 to every MS-series site; SP-MS-PIPE-007 inspection standard new.',
        links: [
          { label: 'report', openDoc: 'doc:IR-2026-014' },
          { label: 'bulletin', openDoc: 'doc:TB-2026-033' },
        ],
      },
    ],
    replaySub:
      'Same RMS feed, with the breakout-torque trend and thresholds enabled (as they now are on every connected string)',
    replay: [
      {
        when: '30 Aug',
        agent: 'Diagnostic',
        text: 'Breakout torque +10% over ten days at flat air and penetration → thread wear, Watch at 0.71. Names the string and the pattern.',
      },
      {
        when: '30 Aug',
        agent: 'Knowledge',
        text: 'Attaches SP-MS-PIPE-007 gauging and the 8.5 kN·m make-up rule; flags that the wrench readout, not feel, sets the torque.',
      },
      {
        when: '31 Aug',
        agent: 'Work order',
        text: 'Drafts a thread-gauge task for the next PM-250 (45 min) and a pipe swap if any pin fails.',
      },
      {
        when: '31 Aug',
        agent: 'Parts & logistics',
        text: 'OH store holds 2 pipes against a likely 3 → drafts a top-up from Darwin, road, 3 days — arrives before the swap.',
      },
      {
        when: '03 Sep',
        agent: 'Orchestrator',
        text: 'Torque crosses 9.5 kN·m → Act. Pulls the string at the shift change: two pipes condemned, string re-made at 8.5. 1.6 h.',
      },
      {
        when: '03 Sep',
        agent: 'Technician copilot',
        text: 'Make-up torque per joint read back from the wrench on the headset; over-torque prompt to the operator.',
      },
      {
        when: '05 Sep',
        agent: 'Fleet learning',
        text: 'No parted string. No fishing job. A$4,600 planned against A$24,300 as it happened; the thread-wear rule is applied to all eleven surface drills.',
      },
    ],
  },
  {
    id: 'IR-2026-015',
    ref: 'IR-2026-015 · WO-26-11884',
    machine: 'L-18',
    mine: 'GD',
    comp: 'Engine',
    date: '07 Sep 2026',
    title: 'Engine bay fire — suppression discharged, operator out unharmed',
    status: 'Closed · incident review open (CS-2026-2218)',
    workOrder: 'WO-26-11884',
    sub: "Sun 07 Sep 2026, Gawler Deep (SA), northern workings decline, 640 level — a contractor-maintained loader. A boom-lift return hose burst onto the turbocharger and ignited; the suppression discharged automatically. Live telematics and the operator's own top-up log had the leak for four days. Left: what happened. Right: the same days with the top-up rule and the hose-interval reconciliation in place.",
    kpis: [
      {
        label: 'Downtime',
        value: { kind: 'text', text: '43.5' },
        unit: 'hours',
        source: { dataset: 'wos', filter: { wo_id: 'WO-26-11884' } },
        note: [
          txt('07 Sep 10:12 → 09 Sep 05:40 · '),
          cost('downtime_cost_aud'),
          txt(' lost production at A$1,800/h (loader, Gawler Deep)'),
        ],
      },
      {
        label: 'All-in cost',
        value: { kind: 'cost', field: 'all_in_cost_aud' },
        unit: 'vs ≈ A$4,100 planned hose kit',
        source: { dataset: 'wos', filter: { wo_id: 'WO-26-11884' } },
        calc: [
          txt('Labour '),
          cost('labour_cost_aud'),
          txt(' + parts '),
          cost('parts_cost_aud'),
          txt(' (turbo, hose kit, suppression, oil) + freight '),
          cost('freight_cost_aud'),
          txt(' + lost production '),
          cost('downtime_cost_aud'),
        ],
        note: [
          txt('Labour '),
          cost('labour_cost_aud'),
          txt(' · parts '),
          cost('parts_cost_aud'),
          txt(' · freight '),
          cost('freight_cost_aud'),
        ],
      },
      {
        label: 'First warning in data',
        value: { kind: 'text', text: '4' },
        unit: 'days before the fire (03 Sep)',
        note: [
          txt(
            'Hydraulic top-ups 2 → 14 L per shift in the plods; W-HYD-LEVEL-LOW ×3 in the live feed; hose kit 1,400 h past interval',
          ),
        ],
      },
      {
        label: 'Attribution',
        value: { kind: 'text', text: 'Contract PM plan', emphasis: 'crit' },
        unit: 'hose kit at 6,000 h vs OEM 4,000 h',
        note: [
          txt('Spinifex North 1 · reconciled at the debrief · fleet hose campaign drafted (WO-26-11916 first)'),
        ],
      },
    ],
    chart: {
      title: 'Hydraulic oil top-ups per day · L-18 (operator plods + live level warnings)',
      subText: 'The leak, as the data saw it. Nothing else on site is above 2 L · ',
      subSource: { dataset: 'shift', filter: { machine_id: 'L-18' } },
      series: [
        {
          source: { kind: 'incidentSeries', machine: 'L-18' },
          colour: 'var(--warn)',
          area: true,
          dots: true,
        },
      ],
      opts: {
        vline: 9,
        vlabel: '07 Sep fire',
        xTitle: 'Date (2026)',
        yTitle: 'Top-up (L per day)',
        hline: 5,
        hlabel: '5 L Watch',
        min: 0,
      },
    },
    caption:
      'Component location: engine bay — turbocharger and the boom-lift return hose where it crosses the frame',
    happened: [
      {
        when: 'Aug',
        text: 'Hose kit on L-18 passes 1,000 h over the OEM 4,000 h interval. The contract PM plan has it at 6,000 h; nobody had reconciled the two.',
      },
      {
        when: '03 Sep',
        text: 'First hydraulic top-up above 2 L logged on the plod. W-HYD-LEVEL-LOW on the live feed on 04, 05 and 06 Sep — advisory, not raised.',
      },
      {
        when: '04–06 Sep',
        text: 'Top-ups 6, 7, 9, 11, 14 L. The operator logs every one. Nothing turns it into a defect.',
      },
      {
        when: '07 Sep 10:12',
        text: 'Hose bursts onto the turbo, loaded on the decline. E-ENG-BAY-TEMP, suppression discharges at 10:12:09, operator fires the second shot, parks and walks. ERT has it out at 10:31.',
        emphasis: ['Hose bursts onto the turbo, loaded on the decline.'],
        links: [{ label: 'email', openDoc: 'eml:17' }],
      },
      {
        when: '14:20',
        text: 'Incident review opened. The platform already has the hose-kit ages for every ML-21 on site and drafts the campaign order, L-17 first.',
        links: [{ label: 'email', openDoc: 'eml:18' }],
      },
      {
        when: '08 Sep 09:00',
        text: 'Debrief: contractor owns the interval, owner adopts the 5 L top-up rule, campaign scheduled across two weekends.',
        links: [{ label: 'debrief', openDoc: 'mtg:MTG-2026-0908B.txt' }],
      },
      {
        when: '09 Sep 05:40',
        text: 'Return to service after turbo, harness, ten hoses, heat shields, recharge and a heat-soak test.',
        links: [
          { label: 'report', openDoc: 'doc:IR-2026-015' },
          { label: 'service report', openDoc: 'x:XS-2049' },
        ],
      },
    ],
    replaySub:
      'Same live feed and plods, with the top-up Watch rule and the PM-plan reconciliation the platform now runs',
    replay: [
      {
        when: 'Aug',
        agent: 'Fleet learning',
        text: 'Holds the contract PM plan and the OEM plan side by side: 4 ML-21 hose kits past the OEM interval on site → campaign proposal to the owner and the contractor.',
      },
      {
        when: '03 Sep',
        agent: 'Diagnostic',
        text: 'Top-up 3 L and a level warning on a machine with a hose kit past interval → Watch, 0.58; names the boom circuit as the likely leak.',
      },
      {
        when: '04 Sep',
        agent: 'Work order',
        text: 'Drafts a hose walk-around at the next crib (20 min) — finds the chafe at the frame crossing.',
      },
      {
        when: '04 Sep',
        agent: 'Parts & logistics',
        text: 'GD store 0 hose kits → drafts a kit from Adelaide DC, road, 2 days.',
      },
      {
        when: '05 Sep',
        agent: 'Safety & compliance',
        text: 'Top-up 6 L → Act: machine held off the decline until the hose is changed; suppression detection loop test added to the job.',
      },
      {
        when: '06 Sep',
        agent: 'Technician copilot',
        text: 'Hose kit fitted with abrasion sleeves at the crossing, 6 h at the weekend; photos and torque values captured on the headset.',
      },
      {
        when: '07 Sep',
        agent: 'Orchestrator',
        text: 'No fire. A$4,100 against A$98,900 as it happened. The interval gap is closed for the whole contract fleet, not one machine.',
      },
    ],
  },
];

/** The incident the page opens on (`renderIncident(INCIDENTS[0].id)`). */
export const DEFAULT_INCIDENT_ID: string = 'WO-26-11842';
